import React, { useCallback, useEffect, useRef, useState } from "react";
import { TRICKS_T } from "./tricks-i18n";
import { LANGUAGES, fmt } from "./i18n";

// Hidden tricks: a fake terminal (~), the Konami code (Matrix rain), a barrel roll
// (click the avatar 5 times) and a hacker mode. They stay hidden and switched off
// until the visitor's 7th visit, which shows a popup offering to list them. Other components trigger tricks with
// window.dispatchEvent(new CustomEvent("trick", { detail: "roll" })).

const INTRO_KEY = "tricksIntroSeen";
const VISITS_KEY = "siteVisits";
const SESSION_KEY = "siteVisitCounted";
const UNLOCK_AT_VISIT = 7;
const KONAMI = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];
const PROMPT = "visitor@chouaibcher:~$";
const TRICK_ICONS = { terminal: "💻", matrix: "🟩", roll: "🌀", hack: "🕵️", light: "☀️" };

function isTyping(e) {
  const el = e.target;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
}

function readSeen() {
  try { return localStorage.getItem(INTRO_KEY) === "yes"; } catch { return false; }
}

function markSeen() {
  try { localStorage.setItem(INTRO_KEY, "yes"); } catch { /* storage unavailable */ }
}

// Count one visit per browser session (refreshing the page doesn't add a visit).
function countVisit() {
  try {
    let visits = parseInt(localStorage.getItem(VISITS_KEY), 10) || 0;
    if (!sessionStorage.getItem(SESSION_KEY)) {
      visits += 1;
      localStorage.setItem(VISITS_KEY, String(visits));
      sessionStorage.setItem(SESSION_KEY, "yes");
    }
    return visits;
  } catch {
    return 0; // storage blocked: keep the tricks hidden
  }
}

function prefersReducedMotion() {
  return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
}

/* ── Matrix rain ─────────────────────────────────────────────────── */
function MatrixRain({ onDone }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const chars = "アイウエオカキクケコサシスセソタチツテトナニヌネノ0123456789ABCDEF<>{}[]/*+-=$#";
    const size = 16;
    let w, h, drops, raf;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      drops = Array.from({ length: Math.ceil(w / size) }, () => Math.floor((Math.random() * -h) / size));
    };
    resize();
    window.addEventListener("resize", resize);

    let last = 0;
    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      if (now - last < 45) return;
      last = now;
      ctx.fillStyle = "rgba(0, 0, 0, 0.08)";
      ctx.fillRect(0, 0, w, h);
      ctx.font = `${size}px monospace`;
      drops.forEach((y, i) => {
        const ch = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillStyle = Math.random() > 0.95 ? "#d1fae5" : "#22c55e";
        ctx.fillText(ch, i * size, y * size);
        drops[i] = y * size > h && Math.random() > 0.975 ? 0 : y + 1;
      });
    };
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, w, h);
    raf = requestAnimationFrame(draw);

    const stop = setTimeout(onDone, prefersReducedMotion() ? 2500 : 9000);
    const onKey = (e) => { if (e.key === "Escape") onDone(); };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(stop);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", onKey);
    };
  }, [onDone]);

  return <canvas ref={canvasRef} className="matrix-rain" onClick={onDone} aria-hidden="true" />;
}

/* ── Terminal ────────────────────────────────────────────────────── */
function Terminal({ tt, t, lang, projects, skills, links, onClose, actions, initialCommand }) {
  const [lines, setLines] = useState(() => [
    { text: tt.term.welcome, kind: "info" },
    { text: tt.term.hint, kind: "muted" },
  ]);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [histPos, setHistPos] = useState(-1);
  const inputRef = useRef(null);
  const screenRef = useRef(null);
  const timers = useRef([]);

  const print = useCallback((items) => {
    setLines((prev) => [...prev, ...items.map((x) => (typeof x === "string" ? { text: x } : x))]);
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
    const list = timers.current;
    return () => list.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (screenRef.current) screenRef.current.scrollTop = screenRef.current.scrollHeight;
  }, [lines]);

  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };

  const run = useCallback((raw) => {
    const cmdline = raw.trim();
    print([{ text: `${PROMPT} ${cmdline}`, kind: "cmd" }]);
    if (!cmdline) return;
    const [cmd, ...args] = cmdline.split(/\s+/);
    const name = cmd.toLowerCase();
    const term = tt.term;

    if (name === "sudo") return print([{ text: term.sudo, kind: "warn" }]);

    switch (name) {
      case "help": {
        const width = Math.max(...Object.keys(term.cmds).map((c) => c.length)) + 2;
        return print([
          { text: term.helpTitle, kind: "info" },
          ...Object.entries(term.cmds).map(([c, d]) => ({ text: `  ${c.padEnd(width)}${d}` })),
        ]);
      }
      case "whoami":
        return print([{ text: "Chouaib Cheribet Cherif", kind: "ok" }, t.tagline]);
      case "about":
        return print([t.aboutP1, t.aboutP2]);
      case "projects":
        return print(
          projects.flatMap((p) => [
            { text: `• ${p.title} — ${p.subtitle}`, kind: "ok" },
            { text: `  ${p.comingSoon ? t.comingSoon : p.link}`, kind: "muted" },
          ])
        );
      case "skills":
        return print(Object.entries(skills).map(([g, items]) => ({ text: `${t.skillGroups[g]}: ${items.join(", ")}` })));
      case "contact":
        return print([
          `email     ${links.email.replace("mailto:", "")}`,
          `github    ${links.github}`,
          `linkedin  ${links.linkedin}`,
        ]);
      case "open": {
        const target = (args[0] || "").toLowerCase();
        if (!links[target]) return print([{ text: term.openUsage, kind: "muted" }]);
        if (target === "email") window.location.href = links.email;
        else window.open(links[target], "_blank", "noopener");
        return print([{ text: `→ ${links[target]}`, kind: "ok" }]);
      }
      case "lang": {
        const code = (args[0] || "").toLowerCase();
        const info = LANGUAGES.find((l) => l.code === code);
        if (!info) return print([{ text: fmt(term.langUsage, { codes: LANGUAGES.map((l) => l.code).join(" | ") }), kind: "muted" }]);
        actions.setLang(code);
        return print([{ text: fmt(TRICKS_T[code].term.langChanged, { name: info.name }), kind: "ok" }]);
      }
      case "date":
        return print([new Date().toLocaleString(lang)]);
      case "echo":
        return print([args.join(" ")]);
      case "clear":
        return setLines([]);
      case "exit":
        print([term.bye]);
        return later(onClose, 300);
      case "matrix":
        print([{ text: term.matrixOn, kind: "ok" }]);
        return later(() => { onClose(); actions.matrix(); }, 500);
      case "roll":
        print([{ text: term.rollOn, kind: "ok" }]);
        return later(() => { onClose(); actions.roll(); }, 500);
      case "hack": {
        term.hackSteps.forEach((step, i) => {
          later(() => print([{ text: `[${"█".repeat(i + 1)}${"░".repeat(term.hackSteps.length - i - 1)}] ${step}`, kind: "ok" }]), 450 * (i + 1));
        });
        return later(() => print([{ text: term.hackDone, kind: "warn" }]), 450 * (term.hackSteps.length + 1));
      }
      default:
        return print([{ text: fmt(term.notFound, { cmd }), kind: "err" }]);
    }
  }, [actions, lang, links, onClose, print, projects, skills, t, tt]);

  // Run a command passed in from the tricks list (e.g. "hack").
  const ranInitial = useRef(false);
  useEffect(() => {
    if (initialCommand && !ranInitial.current) {
      ranInitial.current = true;
      run(initialCommand);
    }
  }, [initialCommand, run]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      run(input);
      if (input.trim()) setHistory((h) => [...h, input]);
      setHistPos(-1);
      setInput("");
    } else if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowUp" && history.length) {
      e.preventDefault();
      const pos = histPos === -1 ? history.length - 1 : Math.max(0, histPos - 1);
      setHistPos(pos);
      setInput(history[pos]);
    } else if (e.key === "ArrowDown" && histPos !== -1) {
      e.preventDefault();
      const pos = histPos + 1;
      if (pos >= history.length) { setHistPos(-1); setInput(""); }
      else { setHistPos(pos); setInput(history[pos]); }
    }
  };

  return (
    <div className="term-overlay" onClick={onClose}>
      <div className="term" dir="ltr" role="dialog" aria-modal="true" aria-label="Terminal" onClick={(e) => { e.stopPropagation(); inputRef.current?.focus(); }}>
        <div className="term-bar">
          <span className="term-dot red" onClick={onClose} title={tt.close} />
          <span className="term-dot yellow" />
          <span className="term-dot green" />
          <span className="term-title">{PROMPT.replace(":~$", "")} — zsh</span>
        </div>
        <div className="term-screen" ref={screenRef}>
          {lines.map((l, i) => (
            <div key={i} className={`term-line ${l.kind || ""}`}>{l.text}</div>
          ))}
          <div className="term-input-row">
            <span className="term-prompt">{PROMPT}</span>
            <input
              ref={inputRef}
              className="term-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              aria-label="Terminal input"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── First-visit popup and the tricks list ───────────────────────── */
function TricksIntro({ tt, visits, onShow, onLater }) {
  return (
    <div className="tricks-overlay" onClick={onLater}>
      <div className="tricks-card intro" role="dialog" aria-modal="true" aria-label={tt.introTitle} onClick={(e) => e.stopPropagation()}>
        <div className="tricks-hat" aria-hidden="true">🎩</div>
        <div className="tricks-unlocked">{fmt(tt.unlocked, { n: visits })}</div>
        <h3 className="tricks-title">{tt.introTitle}</h3>
        <p className="tricks-text">{tt.introText}</p>
        <div className="tricks-actions">
          <button type="button" className="tricks-btn primary" onClick={onShow} autoFocus>{tt.showMe}</button>
          <button type="button" className="tricks-btn" onClick={onLater}>{tt.later}</button>
        </div>
      </div>
    </div>
  );
}

function TricksPanel({ tt, onClose, onTry }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="tricks-overlay" onClick={onClose}>
      <div className="tricks-card" role="dialog" aria-modal="true" aria-label={tt.panelTitle} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tricks-close" onClick={onClose} aria-label={tt.close}>✕</button>
        <h3 className="tricks-title">{tt.panelTitle}</h3>
        <ul className="tricks-list">
          {Object.entries(tt.items).map(([key, item]) => (
            <li key={key} className="tricks-item">
              <span className="tricks-icon" aria-hidden="true">{TRICK_ICONS[key]}</span>
              <div className="tricks-item-text">
                <b>{item.title}</b>
                <span>{item.how}</span>
              </div>
              <button type="button" className="tricks-btn small" onClick={() => onTry(key)}>{tt.tryIt}</button>
            </li>
          ))}
        </ul>
        <p className="tricks-hint">{tt.panelHint}</p>
      </div>
    </div>
  );
}

/* ── Everything together ─────────────────────────────────────────── */
export default function TricksLayer({ lang, setLang, t, projects, skills, links, onLightPrank }) {
  const tt = TRICKS_T[lang] || TRICKS_T.en;
  const [visits] = useState(countVisit);
  const unlocked = visits >= UNLOCK_AT_VISIT;
  const [terminal, setTerminal] = useState(null); // null | { cmd }
  const [matrix, setMatrix] = useState(false);
  const [intro, setIntro] = useState(false);
  const [panel, setPanel] = useState(false);

  const roll = useCallback(() => {
    document.body.classList.remove("barrel-roll");
    void document.body.offsetWidth; // restart the animation
    document.body.classList.add("barrel-roll");
    setTimeout(() => document.body.classList.remove("barrel-roll"), 1300);
  }, []);

  const startMatrix = useCallback(() => setMatrix(true), []);
  const stopMatrix = useCallback(() => setMatrix(false), []);
  const closeTerminal = useCallback(() => setTerminal(null), []);

  const actions = useRef({});
  actions.current = { setLang, matrix: startMatrix, roll };
  const stableActions = useRef({
    setLang: (c) => actions.current.setLang(c),
    matrix: () => actions.current.matrix(),
    roll: () => actions.current.roll(),
  }).current;

  const tryTrick = useCallback((key) => {
    setPanel(false);
    if (key === "terminal") setTerminal({ cmd: null });
    else if (key === "hack") setTerminal({ cmd: "hack" });
    else if (key === "matrix") startMatrix();
    else if (key === "roll") roll();
    else if (key === "light") onLightPrank();
  }, [onLightPrank, roll, startMatrix]);

  // Keyboard: ~ or ` opens the terminal, the Konami code starts the Matrix.
  useEffect(() => {
    if (!unlocked) return undefined;
    let pos = 0;
    const onKey = (e) => {
      if (document.documentElement.classList.contains("quiz-locked")) return;
      if (!isTyping(e) && (e.key === "`" || e.key === "~")) {
        e.preventDefault();
        setTerminal((cur) => (cur ? null : { cmd: null }));
        return;
      }
      if (isTyping(e)) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      pos = key === KONAMI[pos] ? pos + 1 : key === KONAMI[0] ? 1 : 0;
      if (pos === KONAMI.length) { pos = 0; startMatrix(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [startMatrix, unlocked]);

  // Other components can trigger a trick by name.
  useEffect(() => {
    if (!unlocked) return undefined;
    const onTrick = (e) => tryTrick(e.detail);
    window.addEventListener("trick", onTrick);
    return () => window.removeEventListener("trick", onTrick);
  }, [tryTrick, unlocked]);

  // The visit that unlocks the tricks: once the quiz is passed, ask whether to show them.
  useEffect(() => {
    if (!unlocked || readSeen()) return undefined;
    let timer;
    const show = () => { timer = setTimeout(() => setIntro(true), 900); };
    if (!document.documentElement.classList.contains("quiz-locked")) show();
    window.addEventListener("quiz-passed", show);
    return () => { clearTimeout(timer); window.removeEventListener("quiz-passed", show); };
  }, [unlocked]);

  const projectList = Object.values(projects).flat().map((p) => ({ ...p, ...t.projects[p.id] }));

  if (!unlocked) return null;

  return (
    <>
      <button type="button" className="tricks-fab" onClick={() => setPanel(true)} aria-label={tt.open} title={tt.open}>🎩</button>

      {intro && (
        <TricksIntro
          tt={tt}
          visits={visits}
          onShow={() => { markSeen(); setIntro(false); setPanel(true); }}
          onLater={() => { markSeen(); setIntro(false); }}
        />
      )}
      {panel && <TricksPanel tt={tt} onClose={() => setPanel(false)} onTry={tryTrick} />}
      {terminal && (
        <Terminal
          key={terminal.cmd || "term"}
          tt={tt}
          t={t}
          lang={lang}
          projects={projectList}
          skills={skills}
          links={links}
          actions={stableActions}
          onClose={closeTerminal}
          initialCommand={terminal.cmd}
        />
      )}
      {matrix && <MatrixRain onDone={stopMatrix} />}
    </>
  );
}
